import {
  AbilityBuilder,
  PureAbility,
  createMongoAbility,
  fieldPatternMatcher,
  type MongoAbility,
} from '@casl/ability'
import { useAbility as useCaslAbility } from '@casl/react'
import { createContext, useContext, type ReactNode } from 'react'

import { getRouteApi, useRouterState } from '@tanstack/react-router'

// Permissions arrive from the API as `{action}:{subject}[:field]` strings, so actions and
// subjects are open-ended strings. The app builds it with `defineAbility(..., abilityOptions)`.
export type AppAbility = MongoAbility<[string, string]>

// The only part of the authenticated user that `updateAbility` reads.
export type AbilityUser = {
  permissions?: readonly string[] | null
}

// casl's default `anyAction`/`anySubjectType`; `abilityOptions` does not override them.
const ANY_ACTION = 'manage'
const ANY_SUBJECT = 'all'
const ROOT_ROUTE_ID = '__root__'

export const abilityOptions = {
  fieldMatcher: fieldPatternMatcher,
}

// Consumers outside a provider see an ability that allows nothing.
export const AbilityContext = createContext<AppAbility>(
  createMongoAbility<AppAbility>([], abilityOptions)
)

type ReadOnlyContextValue = {
  isReadOnly?: boolean
  overridesRoute?: boolean
}

const ReadOnlyContext = createContext<ReadOnlyContextValue | undefined>(undefined)
const LEGACY_USERS_ALIAS_FIELD = '__legacy_alias:organization-users'

export const useAbility = (): AppAbility => {
  return useCaslAbility(AbilityContext)
}

export function hasBroadPermission(ability: AppAbility, action: string, subject: string): boolean {
  if (!ability.can(action, subject)) {
    return false
  }

  if (typeof ability.rulesFor !== 'function') {
    return true
  }

  const broadRules = ability
    .rulesFor(action, subject)
    .filter((rule) => !rule.inverted && (!rule.fields || rule.fields.length === 0))

  return broadRules.length > 0
}

function readLoaderIsReadOnly(data: unknown): unknown {
  if ((typeof data !== 'object' && typeof data !== 'function') || data === null) {
    return undefined
  }

  return 'isReadOnly' in data ? data.isReadOnly : undefined
}

type RouteIsReadOnlyOptions = { shouldThrow?: boolean }

export const useRouteIsReadOnly = ({ shouldThrow = true }: RouteIsReadOnlyOptions = {}):
  | boolean
  | undefined => {
  // With no matches there is no route; the root lookup throws as the nearest-match lookup did.
  const route = getRouteApi(
    useRouterState({
      select: (state) => state.matches[state.matches?.length - 1]?.routeId,
    }) ?? ROOT_ROUTE_ID
  )
  if (!route) {
    if (shouldThrow) throw new Error('useIsReadOnly must be used within a Route')
    else return
  }

  const data: unknown = route.useLoaderData()
  if (!data) {
    if (shouldThrow)
      throw new Error(
        'useIsReadOnly needs a Route that returns {isReadOnly} from the loader function'
      )
    return
  }
  const isReadOnly = readLoaderIsReadOnly(data)

  if (isReadOnly === undefined && shouldThrow) {
    if (shouldThrow)
      throw new Error(
        'useIsReadOnly needs a Route that returns {isReadOnly} from the loader function'
      )
    return
  }

  // Every reader only checks `=== undefined` or truthiness, so coercing a defined value is safe.
  return isReadOnly === undefined ? undefined : Boolean(isReadOnly)
}

type ReadOnlyProps = {
  children?: ReactNode
  isReadOnly?: boolean
  overrideRoute?: boolean
}

export function ReadOnly({ children, isReadOnly, overrideRoute = false }: ReadOnlyProps) {
  const routeIsReadOnly = useRouteIsReadOnly({ shouldThrow: false })
  const resolvedIsReadOnly = overrideRoute ? isReadOnly : routeIsReadOnly || isReadOnly

  return (
    <ReadOnlyContext.Provider
      value={{ isReadOnly: resolvedIsReadOnly, overridesRoute: overrideRoute }}
    >
      {children}
    </ReadOnlyContext.Provider>
  )
}

export const useIsReadOnly = (): { isReadOnly: boolean } => {
  const { isReadOnly, overridesRoute } = useContext(ReadOnlyContext) || {}
  const routeIsReadOnly = useRouteIsReadOnly({ shouldThrow: false })

  if (routeIsReadOnly === undefined && isReadOnly === undefined) {
    throw new Error(
      'useIsReadOnly must be used within a Route that returns {isReadOnly} from the loader function or ReadOnly component'
    )
  }

  return {
    isReadOnly: overridesRoute ? Boolean(isReadOnly) : Boolean(routeIsReadOnly || isReadOnly),
  }
}

export const useOptionalIsReadOnly = (): { isReadOnly: boolean } => {
  const { isReadOnly, overridesRoute } = useContext(ReadOnlyContext) || {}
  const routeIsReadOnly = useRouteIsReadOnly({ shouldThrow: false })

  return {
    isReadOnly: overridesRoute ? Boolean(isReadOnly) : Boolean(routeIsReadOnly || isReadOnly),
  }
}

type CanActionProps = { I: string; do?: string } | { do: string; I?: string }

type CanSubjectAlias = 'the' | 'a' | 'an' | 'these' | 'this' | 'of' | 'on'

// One subject alias is required; every other alias stays optional.
type CanSubjectProps = {
  [Alias in CanSubjectAlias]: Record<Alias, string> &
    Partial<Record<Exclude<CanSubjectAlias, Alias>, string>>
}[CanSubjectAlias]

export type CanProps = CanActionProps &
  CanSubjectProps & {
    field?: string
    not?: boolean
    passThrough?: boolean
    children: ReactNode | ((isAllowed: boolean, ability: AppAbility) => ReactNode)
  }

export function Can({
  do: doAction,
  I,
  of,
  on,
  a,
  an,
  this: thisSubject,
  these,
  the,
  field,
  not = false,
  passThrough = false,
  children,
}: CanProps): ReactNode {
  const ability = useAbility()
  // casl reads a missing action as `anyAction` and a missing subject as `anySubjectType`.
  const action = (doAction || I) ?? ANY_ACTION
  const subject = (the || a || an || these || thisSubject || of || on) ?? ANY_SUBJECT
  let isAllowed = ability[not ? 'cannot' : 'can'](action, subject, field)

  if (!field && !not && isAllowed && typeof ability.rulesFor === 'function') {
    isAllowed = hasBroadPermission(ability, action, subject)
  }

  if (!passThrough && !isAllowed) {
    return null
  }

  return typeof children === 'function' ? children(isAllowed, ability) : children
}

type AliasSubject = { subject: string; field?: string }

export function updateAbility(user: AbilityUser | null | undefined, ability: AppAbility): void {
  const { can, rules } = new AbilityBuilder<AppAbility>(PureAbility)

  const aliasSubjects = (action: string, subject: string): AliasSubject[] => {
    if (subject === 'organization-users') {
      return [{ subject }, { subject: 'users', field: LEGACY_USERS_ALIAS_FIELD }]
    }

    if (subject === 'global-users') {
      return [{ subject }]
    }

    if (subject === 'global-roles' || subject === 'organization-roles') {
      return [{ subject }, { subject: 'roles' }]
    }

    return [{ subject }]
  }

  // permissions come from the API in the form `{action}:{subject}` (e.g. create:users)
  // Wildcards are also supported (e.g. *:users or view:*)
  // casl needs them split into separate parts
  // Also, casl uses the word 'manage' instead of '*' for the action
  // and the word 'all' instead of '*' for the subject
  const permissions = user?.permissions?.map((permission) => {
    // `split` always returns at least one part, so the `action` default never applies.
    const [action = '', subject, ...fieldParts] = permission.split(':')
    const field = fieldParts.length > 0 ? fieldParts.join(':') : undefined
    // A permission without a subject has no field and applies to every subject, which
    // casl indexes as 'all' either way.
    // prettier-ignore
    return {
            action: action === '*' ? ANY_ACTION : action,
            subject: subject === '*' || subject === undefined ? ANY_SUBJECT : subject,
            field: field === '*' ? undefined : field,
        }
  })

  permissions?.forEach(({ action, subject, field }) => {
    aliasSubjects(action, subject).forEach((alias) => {
      const resolvedField = field ?? alias.field

      if (resolvedField) {
        can(action, alias.subject, resolvedField)
        return
      }

      can(action, alias.subject)
    })
  })

  ability.update(rules)
}
